import clsx from 'clsx';
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';

import IconArrowHorizontal from '@/assets/icons/icon-arrow-horizontal.svg?react';
import IconArrowVertical from '@/assets/icons/icon-arrow-vertical.svg?react';
import IconArrowHorizontalFilled from '@/assets/icons/icon-arrow-horizontal-filled.svg?react';
import IconArrowVerticalFilled from '@/assets/icons/icon-arrow-vertical-filled.svg?react';

interface ContentDirectionProps {
  text?: string;
  align: 'vertical' | 'horizontal';
  onClick?: (size: 'vertical' | 'horizontal') => void;
  className?: string;
}

const aligns: Array<'vertical' | 'horizontal'> = ['vertical', 'horizontal'];

export const ContentDirection: React.FC<ContentDirectionProps> = ({
  align = 'vertical',
  onClick,
  className,
  text = 'Align',
}) => {
  const [selectedSize, setSelectedSize] = useState<'vertical' | 'horizontal'>(align);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);

  const optionRefs = useRef<Record<'vertical' | 'horizontal', HTMLButtonElement | null>>({
    vertical: null,
    horizontal: null,
  });

  useEffect(() => {
    if (align !== selectedSize) {
      setSelectedSize(align);
    }
  }, [align]);

  useLayoutEffect(() => {
    const activeEl = optionRefs.current[selectedSize];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [selectedSize]);

  const handleClick = (newSize: 'vertical' | 'horizontal') => {
    setIsAnimated(true);
    setSelectedSize(newSize);
    if (onClick) onClick(newSize);
  };

  return (
    <div className={clsx('flex items-center justify-between gap-4', className)}>
      {text && <p className="text-button-sm font-medium text-neutral-500">{text}</p>}

      <div className="relative flex h-8 items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
        <div
          className={clsx(
            'absolute top-0 z-20 h-full rounded-md bg-[#ffffff] outline outline-offset-[-1px] outline-[#e9ecf0]',
            isAnimated ? 'transition-all duration-300' : '',
          )}
          style={{
            width: highlightStyle.width,
            transform: `translateX(${highlightStyle.left}px)`,
          }}
        />

        {aligns.map((sz) => {
          const Icon =
            sz === 'vertical'
              ? selectedSize === sz
                ? IconArrowVerticalFilled
                : IconArrowVertical
              : selectedSize === sz
                ? IconArrowHorizontalFilled
                : IconArrowHorizontal;

          const iconClass = selectedSize === sz ? 'text-indigo-500' : 'text-neutral-400';

          return (
            <button
              key={sz}
              ref={(el) => {
                optionRefs.current[sz] = el;
              }}
              onClick={() => handleClick(sz)}
              className="relative z-20 flex h-8 w-8 cursor-pointer items-center justify-center"
              type="button"
            >
              <Icon className={`h-4.5 w-4.5 ${iconClass}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
};
